// 클레임 다건등록(엑셀 · 구성안 화면②) 순수 함수 — 판정·이슈 라벨 · 요약 건수 · 증빙 현황 · 선택 요약 · 상세 패널 수정 칸
// · 단건 화면 prefill(오류 행 → /claims/register?bulk_upload&bulk_row) · 올릴 파일 검사.
// 서버 routers/claim_bulk.py · services/claim_bulk(.row_dict · upload_dict · row_detail) 와 칸 이름이 같다.
// 🔴 판정·금액·기한·승인은 서버 값만 쓴다 — 여기서 셈하지 않는다(건수 표기와 증빙 완료 비율만).
import { EMPTY_DRAFT, blankRow, checkInvoice, intOrNull, rowsFromClaim, rowsFromLookup } from './claimRegisterUtil';

export const PAGE_SIZE = 50;                       // 서버 claim_bulk.PAGE_SIZE 와 같다
export const FAILED_PAGE = 200;                    // 서버 claim_bulk.PAGE_MAX — 등록 실패 행 사유는 앞 200행에서 찾는다
export const POLL_MS = 1500;                       // 검증·등록 중 GET /{id} 간격
export const FILE_BYTES_MAX = 2 * 1024 * 1024;     // 서버 claim_bulk_sheet.FILE_BYTES_MAX 와 같다
export const ROWS_MAX = 1000;                      // 서버 claim_bulk_sheet.ROWS_MAX(넘으면 서버가 422 too_many_rows)
export const LIVE_STATUSES = ['queued', 'validating', 'registering'];
export const REGISTERED = ['registered', 'pending_approval', 'approved'];

// 서버 claim_bulk_sheet.check_upload 와 같은 문구.
export const FILE_ERRORS = {
  none: '올릴 엑셀 파일(xlsx)을 고르세요.',
  ext: '엑셀(xlsx) 파일만 올릴 수 있습니다.',
  empty: '빈 파일입니다.',
  size: `엑셀 파일은 ${FILE_BYTES_MAX / (1024 * 1024)}MB 이하여야 합니다.`,
};

export const LEVEL_FILTERS = [
  { id: 'all', level: '', label: '전체', tone: 'gray' },
  { id: 'ok', level: 'ok', label: '정상', tone: 'green' },
  { id: 'warn', level: 'warn', label: '경고', tone: 'yellow' },
  { id: 'error', level: 'error', label: '오류', tone: 'red' },
];
export const LEVEL_LABEL = { pending: '검증 전', ok: '정상', warn: '경고', error: '오류' };
export const UPLOAD_STATUS = {
  queued: '대기', validating: '검증 중', ready: '검증 완료', registering: '등록 중', done: '등록 완료', failed: '실패',
  cancelled: '취소됨',
};
export const UPLOAD_STATUS_TONE = {
  queued: 'gray', validating: 'blue', ready: 'green', registering: 'blue', done: 'green', failed: 'red', cancelled: 'gray',
};
const UPLOAD_STATE_TEXT = {
  queued: '검증 대기', validating: '검증 중', ready: '자동 검증 완료', registering: '등록 중', done: '자동 검증 완료',
  failed: '검증 실패', cancelled: '취소됨',
};
export const REGISTER_LABEL = { registered: '등록완료', pending_approval: '승인대기', approved: '승인', failed: '등록 실패' };
export const ACCIDENT_SHORT = { courier: '택배', general: '일반' };
export const ACCIDENT_TONE = { courier: 'blue', general: 'orange' };
export const ACCIDENT_LABEL = { courier: '택배사고', general: '일반사고' };

// 판정 칩 짧은 글자(서버 claim_bulk.ERROR_LABELS · WARN_LABELS 코드) — 전체 문구는 issues[].message.
// 구성안 «사진 필요»는 경고 «증빙 미비»(등록은 된다).
export const ISSUE_SHORT = {
  invoice_missing: '송장 오류',
  invoice_format: '송장 오류',
  invoice_not_found: '송장 오류',
  lookup_error: '조회 오류',
  required_missing: '필수값 누락',
  blocked: '처리 불가',
  v2_registered: '2.0 중복',
  v2_dup_in_file: '2.0 중복',
  invalid_value: '값 오류',
  evidence_short: '증빙 미비',
  shipper_deadline_overdue: '기한 경과',
  same_invoice: '중복 의심',
  repeat_30d: '반복 클레임',
  dup_in_file: '중복 의심',
  product_select: '상품 선택',
  v2_select: '2.0 선택',
  courier_group_unconfirmed: '택배사 그룹 미확정',
};

// 엑셀 낱말 → 코드값(서버 claim_bulk_sheet.ACCIDENT_WORDS · HANDLING_WORDS 와 같다) — 본문이 없는 오류 행 prefill 용.
const ACCIDENT_WORDS = { 택배사고: 'courier', 일반사고: 'general' };
const HANDLING_WORDS = { 재출고: 'reship', 환불: 'refund' };

const str = (value) => (value === null || value === undefined ? '' : String(value));
const num = (value) => Number(value) || 0;

/** 올릴 파일 검사(서버와 같은 값) — .xlsx · 빈 파일 아님 · 2MB 이하. 문제 없으면 ''. */
export function checkUploadFile(file) {
  const name = file && typeof file.name === 'string' ? file.name.trim() : '';
  if (!name) return FILE_ERRORS.none;
  if (!name.toLowerCase().endsWith('.xlsx')) return FILE_ERRORS.ext;
  if (!file.size) return FILE_ERRORS.empty;
  if (file.size > FILE_BYTES_MAX) return FILE_ERRORS.size;
  return '';
}

/** Content-Disposition filename* → 파일 이름(없으면 fallback). */
export function fileNameOf(response, fallback) {
  const disposition = (response && response.headers && response.headers['content-disposition']) || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (!encoded) return fallback;
  try { return decodeURIComponent(encoded[1].trim()); } catch { return fallback; }
}

export const isLive = (upload) => !!upload && LIVE_STATUSES.includes(upload.status);

/** 업로드 → 필터 칩·⑤ 요약 건수 {all, ok, warn, error}(서버 counts · 전체 = total_rows). */
export function summaryCounts(upload) {
  const counts = (upload && upload.counts) || {};
  return { all: num(upload && upload.total_rows), ok: num(counts.ok), warn: num(counts.warn), error: num(counts.error) };
}

/** ⑥ 증빙사진 첨부 현황 — 서버 evidence {done, missing}(정상·경고 행) → {done, total, pct}. */
export function evidenceProgress(upload) {
  const ev = (upload && upload.evidence) || {};
  const done = num(ev.done);
  const total = done + num(ev.missing);
  return { done, total, pct: total ? Math.round((done * 100) / total) : 0 };
}

/** ⑦ «선택 17건 · 택배사고 9 / 일반사고 8». */
export function selectionText(selection) {
  const s = selection || {};
  return `선택 ${num(s.selected)}건 · 택배사고 ${num(s.courier)} / 일반사고 ${num(s.general)}`;
}

/** [선택 건 일괄 등록] 확인 문구 «17건 등록(택배사고 9 · 일반사고 8)». */
export function registerConfirmText(selection) {
  const s = selection || {};
  return `${num(s.selected)}건 등록(택배사고 ${num(s.courier)} · 일반사고 ${num(s.general)})`;
}

/** 일괄 등록 결과 «등록완료 N · 승인대기 N · 실패 N»(서버 register). */
export function resultText(register) {
  const r = register || {};
  return `등록완료 ${num(r.registered)} · 승인대기 ${num(r.pending)} · 실패 ${num(r.failed)}`;
}

/** 진행 막대 — 검증 중 = checked_rows / total_rows(서버 progress_pct) · 등록 중 = register.done / register.total. */
export function progressOf(upload) {
  if (!upload) return null;
  if (upload.status === 'registering') {
    const r = upload.register || {};
    const total = num(r.total);
    const done = num(r.done);
    return { label: '등록 중', done, total, pct: total ? Math.round((done * 100) / total) : 0 };
  }
  return { label: '검증 중', done: num(upload.checked_rows), total: num(upload.total_rows), pct: num(upload.progress_pct) };
}

/** 업로드 카드 한 줄 «23건 인식 · 자동 검증 완료 · 11-02 10:42 업로드»(시각은 부르는 쪽이 KST 로 바꿔 넘긴다). */
export function uploadInfoText(upload, time) {
  if (!upload) return '';
  return [`${num(upload.total_rows)}건 인식`, UPLOAD_STATE_TEXT[upload.status] || upload.status, time ? `${time} 업로드` : '']
    .filter(Boolean).join(' · ');
}

/** 업로드 이력 «결과» 칸 — 정상·경고·오류 + 등록 결과(있으면). */
export function historyResultText(item) {
  const c = (item && item.counts) || {};
  const r = (item && item.register) || {};
  const parts = [`정상 ${num(c.ok)} · 경고 ${num(c.warn)} · 오류 ${num(c.error)}`];
  if (num(r.registered) || num(r.pending) || num(r.failed)) parts.push(resultText(r));
  return parts.join(' / ');
}

/** 행 체크를 바꿀 수 있는가 — 고칠 수 있는 업로드 · 정상·경고 · 아직 등록 안 됨(서버 select_rows 와 같은 조건). */
export function canSelectRow(row, upload) {
  return !!(upload && upload.can_edit && row && ['ok', 'warn'].includes(row.level) && !REGISTERED.includes(row.register_status));
}

/** 오류 행 송장 → 단건 화면 경로(그 행 값으로 채운다). */
export const fixPath = (uploadId, rowId) => `/claims/register?bulk_upload=${encodeURIComponent(uploadId)}&bulk_row=${encodeURIComponent(rowId)}`;

/** 판정 칩 {label, tone, title} — 등록 상태가 먼저 · 정상 초록 · 경고 노랑 · 오류 빨강(첫 이슈의 짧은 글자 · 전체 문구는 title). */
export function rowStatus(row) {
  const r = row || {};
  const issues = Array.isArray(r.issues) ? r.issues : [];
  const title = issues.map((i) => i.message).filter(Boolean).join('\n');
  if (REGISTERED.includes(r.register_status)) return { label: REGISTER_LABEL[r.register_status], tone: 'blue', title };
  if (r.register_status === 'failed') return { label: REGISTER_LABEL.failed, tone: 'red', title: r.register_error || title };
  if (r.level === 'ok') return { label: LEVEL_LABEL.ok, tone: 'green', title };
  if (r.level !== 'warn' && r.level !== 'error') return { label: LEVEL_LABEL.pending, tone: 'gray', title };
  const first = issues.find((i) => i.level === r.level) || issues[0];
  return { label: (first && ISSUE_SHORT[first.code]) || LEVEL_LABEL[r.level], tone: r.level === 'error' ? 'red' : 'yellow', title };
}

// 행 표에 쓰는 칸(서버 row_dict) — 상세 응답(row_detail)에서 이 칸만 표로 옮긴다.
const ROW_KEYS = [
  'id', 'row_no', 'invoice_no', 'level', 'issues', 'summary', 'raw', 'edits', 'edited', 'selected', 'claim_id',
  'register_status', 'register_error', 'evidence', 'checked_at', 'updated_at',
];
export function rowOf(detail) {
  const out = {};
  ROW_KEYS.forEach((key) => { if (detail && key in detail) out[key] = detail[key]; });
  return out;
}

// 상세 패널에서 고치는 글자 칸(서버 claim_bulk.EDIT_KEYS 중) — reason_id·v2_claim_no·items 는 따로.
export const PANEL_TEXT_KEYS = ['fault', 'extent', 'handling', 'temp_zone', 'center_key', 'memo', 'intake_channel', 'compensation_method'];

/** 행 상세(row_detail) → 상세 패널 시작 값(서버 prefill = 지금 본문 값). */
export function panelFromDetail(detail) {
  const p = (detail && detail.prefill) || {};
  const summary = (detail && detail.summary) || {};
  const out = { accident: summary.accident || EMPTY_DRAFT.accident, reason_id: str(p.reason_id), v2_claim_no: str(p.v2_claim_no) };
  PANEL_TEXT_KEYS.forEach((key) => { out[key] = str(p[key]); });
  out.items = (Array.isArray(p.items) ? p.items : []).map((it) => ({
    line_key: it.line_key || null,
    prod_name: it.prod_name || '',
    prod_cd: it.prod_cd || '',
    out_qty: num(it.out_qty),
    pur_price: it.pur_price ?? null,
    selected: !!it.selected,
    qty: str(it.qty ?? 0),
    unit_price_input: str(it.unit_price_input),
  }));
  return out;
}

/**
 * 상세 패널 → PATCH edits(바뀐 칸만). 사유를 비우면 null(= 자동 값으로) · 2.0 «연결 안 함» = '' ·
 * 상품 줄은 line_key 있는 줄 중 체크·수량·단가가 바뀐 줄만 [{line_key, selected, qty, unit_price_input}].
 */
export function panelEdits(start, panel) {
  const edits = {};
  if (!start || !panel) return edits;
  if (panel.reason_id !== start.reason_id) edits.reason_id = intOrNull(panel.reason_id);
  PANEL_TEXT_KEYS.forEach((key) => { if (panel[key] !== start[key]) edits[key] = panel[key]; });
  if (panel.v2_claim_no !== start.v2_claim_no) edits.v2_claim_no = panel.v2_claim_no;
  const before = new Map((start.items || []).map((it) => [it.line_key, it]));
  const items = [];
  (panel.items || []).forEach((it) => {
    const b = it.line_key ? before.get(it.line_key) : null;
    if (!b) return;
    if (it.selected !== b.selected || it.qty !== b.qty || it.unit_price_input !== b.unit_price_input) {
      items.push({ line_key: it.line_key, selected: !!it.selected, qty: intOrNull(it.qty) ?? 0, unit_price_input: intOrNull(it.unit_price_input) });
    }
  });
  if (items.length) edits.items = items;
  return edits;
}

/**
 * 엑셀 일괄등록 행(row_detail) → 단건 화면에 채울 값 {invoice, invoiceText, lookup, rows, draft, v2}.
 * rows = rowsFromClaim({calc_basis:{items: body.items}}) · draft = EMPTY_DRAFT 칸만 · 조회는 다시 부르지 않는다(lookup = 저장한 스냅샷).
 * 본문이 없는 오류 행(송장 없음·형식·조회 오류)은 원문(raw)의 메모·처리 방식·접수 경로·사고구분과 수기 줄 1개로 채운다.
 */
export function prefillFromBulkRow(row) {
  const data = row || {};
  const body = data.body || null;
  const raw = data.raw || {};
  const summary = data.summary || {};
  const lookup = data.snapshot || null;
  const invoiceText = str(data.invoice_no || raw.invoice_no);
  const checked = checkInvoice(invoiceText);

  let rows = body && Array.isArray(body.items) && body.items.length ? rowsFromClaim({ calc_basis: { items: body.items } }) : [];
  if (!rows.length && lookup && lookup.found && Array.isArray(lookup.items)) rows = rowsFromLookup(lookup.items);
  if (!rows.length) rows = [{ ...blankRow(), prod_name: str(raw.product), qty: str(intOrNull(raw.qty) ?? 1) }];

  const draft = { ...EMPTY_DRAFT };
  if (body) {
    Object.assign(draft, {
      reason_id: str(body.reason_id),
      fault: body.fault || '',
      extent: body.extent || '',
      handling: body.handling || '',
      temp_zone: body.temp_zone || '',
      center_key: body.center_key || '',
      shipper_id: str(body.shipper_id),
      shipper_name: body.shipper_id ? '' : (body.shipper_name || ''),
      ship_date: body.ship_date || '',
      delivered_date: body.delivered_date || '',
      occurred_date: body.occurred_date || '',
      memo: body.memo || '',
      reship_invoice_no: body.reship_invoice_no || '',
      pack_code: body.pack_code || '',
      restocked: !!body.restocked,
      manual_product_amount: str(body.manual_product_amount),
      manual_logistics_amount: str(body.manual_logistics_amount),
      logistics_inputs: Object.fromEntries(Object.entries(body.logistics_inputs || {}).map(([k, v]) => [k, str(v)])),
      intake_channel: body.intake_channel || '',
      compensation_method: body.compensation_method || '',
    });
  } else {
    Object.assign(draft, {
      memo: raw.memo || '',
      handling: HANDLING_WORDS[raw.handling] || '',
      intake_channel: raw.intake_channel || '',
    });
  }
  draft.accident = summary.accident || ACCIDENT_WORDS[raw.accident] || EMPTY_DRAFT.accident;
  const onlyDraft = Object.fromEntries(Object.keys(EMPTY_DRAFT).map((key) => [key, draft[key]]));

  const v2 = body && body.v2_claim_no ? {
    v2_claim_no: body.v2_claim_no,
    v2_reason_cd: body.v2_reason_cd || null,
    v2_reason_detail_cd: body.v2_reason_detail_cd || null,
    v2_claim_type: body.v2_claim_type || null,
  } : null;

  return { invoice: checked.error ? '' : checked.value, invoiceText, lookup, rows, draft: onlyDraft, v2 };
}
